import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User, Bot } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';

const WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || 'http://localhost:5678/webhook-test/a60a1eee-0c42-4b11-97b7-30120a1cf368';

const Chatbot = ({ darkMode = false }) => {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      text: t('chatbot.greeting')
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);
  const chatButtonRef = useRef(null);

  // Check if the webhook backend is reachable on mount
  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    fetch(WEBHOOK_URL, { method: 'GET', signal: controller.signal })
      .then(() => setAvailable(true))
      .catch(() => setAvailable(false))
      .finally(() => {
        clearTimeout(timeout);
        setChecking(false);
      });

    return () => { controller.abort(); clearTimeout(timeout); };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Show prompt after 30 seconds on website
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowPrompt(true);
        // Auto-hide prompt after 30 seconds if not interacted with
        setTimeout(() => {
          setShowPrompt(false);
        }, 30000);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [isOpen]);

  // Click outside to close chat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        chatWindowRef.current &&
        !chatWindowRef.current.contains(event.target) &&
        chatButtonRef.current &&
        !chatButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Dynamic classes based on theme
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const cardBgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-white' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const messagesBgColor = darkMode ? 'bg-gray-850' : 'bg-gray-50';

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: 'user', text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatInput: input }),
      });

      const data = await res.json();
      console.log(data);

      // Simulate typing delay for better UX
      setTimeout(() => {
        const botMessage = {
          sender: 'bot',
          text: data.answer ?? t('chatbot.errorNoAnswer'),
        };
        setMessages((prev) => [...prev, botMessage]);
        setIsTyping(false);
      }, 1000);
    } catch (err) {
      console.error('Chatbot send error:', err);
      setTimeout(() => {
        const errorMessage = {
          sender: 'bot',
          text: t('chatbot.errorGeneral'),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Hide completely while checking or if backend unreachable
  if (checking || !available) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 sm:bottom-6 sm:right-6">
      {/* Proactive Prompt */}
      {showPrompt && !isOpen && (
        <div className={`absolute bottom-16 right-0 sm:bottom-20 ${cardBgColor} ${borderColor} border rounded-2xl shadow-2xl p-3 sm:p-4 w-72 sm:max-w-xs animate-in slide-in-from-right-5 fade-in`}>
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
              <Bot size={14} className="sm:w-4 sm:h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`font-bold text-xs sm:text-sm ${textColor} mb-1`}>{t('chatbot.promptTitle')}</h4>
              <p className="text-xs text-gray-500 mb-2 sm:mb-3">{t('chatbot.promptSubtitle')}</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setShowPrompt(false);
                    setIsOpen(true);
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-2 sm:px-3 py-1 rounded-lg text-xs font-medium hover:shadow-lg transition-all duration-200"
                >
                  {t('chatbot.promptStart')}
                </button>
                <button
                  onClick={() => setShowPrompt(false)}
                  className="text-gray-400 hover:text-gray-600 text-xs px-1 sm:px-2"
                >
                  {t('chatbot.promptLater')}
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowPrompt(false)}
              className="text-gray-400 hover:text-gray-600 text-sm flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Chat Button */}
      {!isOpen && (
        <button
          ref={chatButtonRef}
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-xl transform hover:scale-110 transition-all duration-300 group"
        >
          <MessageCircle size={20} className="sm:w-6 sm:h-6 group-hover:rotate-12 transition-transform duration-300" />
          <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold animate-pulse">
            !
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatWindowRef}
          className={`fixed inset-0 sm:relative sm:inset-auto w-full h-full sm:w-80 md:w-96 sm:h-96 md:h-[28rem] lg:h-[32rem] ${cardBgColor} ${borderColor} sm:border sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden transform transition-all duration-300 animate-in slide-in-from-bottom-5 sm:slide-in-from-right-5`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base">{t('chatbot.headerTitle')}</h3>
                <p className="text-xs opacity-80">{t('chatbot.headerSubtitle')}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-1 rounded-lg transition-colors duration-200"
            >
              <X size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className={`flex-1 p-3 sm:p-4 overflow-y-auto ${messagesBgColor} space-y-2 sm:space-y-3`}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-1`}
              >
                <div className={`flex items-start space-x-1 sm:space-x-2 max-w-[85%] sm:max-w-[80%] ${msg.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 ${msg.sender === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    }`}>
                    {msg.sender === 'user' ? <User size={10} className="sm:w-3 sm:h-3" /> : <Bot size={10} className="sm:w-3 sm:h-3" />}
                  </div>

                  {/* Message Bubble */}
                  <div className={`px-2 sm:px-3 py-2 rounded-xl ${msg.sender === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-white border border-gray-600'
                        : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                    }`}>
                    <div className="text-xs sm:text-sm leading-relaxed">
                      {msg.text.split('\n').map((line, index) => (
                        <p key={index} className={index > 0 ? 'mt-2' : ''}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start animate-in fade-in slide-in-from-bottom-1">
                <div className="flex items-start space-x-1 sm:space-x-2 max-w-[85%] sm:max-w-[80%]">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                    <Bot size={10} className="sm:w-3 sm:h-3" />
                  </div>
                  <div className={`px-2 sm:px-3 py-2 rounded-xl ${darkMode
                      ? 'bg-gray-700 border border-gray-600'
                      : 'bg-white border border-gray-200 shadow-sm'
                    }`}>
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-3 sm:p-4 border-t ${borderColor}`}>
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`flex-1 px-2 sm:px-3 py-2 ${inputBgColor} ${textColor} border ${borderColor} rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-400`}
                placeholder={t('chatbot.placeholder')}
                disabled={isTyping}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isTyping}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-2 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-none"
              >
                <Send size={14} className="sm:w-4 sm:h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {t('chatbot.poweredBy')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;